import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type OrgRole = 'owner' | 'admin' | 'instructor' | 'student';

export interface OrgMembership {
  org_id: string;
  org_name: string;
  org_slug: string;
  role: OrgRole;
}

/**
 * Returns the user's organizations (memberships). The "current" org for MVP
 * is simply the first owned/admin/instructor org, falling back to the first
 * student membership.
 */
export function useCurrentOrg() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['my-orgs', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<OrgMembership[]> => {
      const { data, error } = await supabase
        .from('org_members')
        .select('role, org_id, organizations!inner(id, name, slug, deleted_at)')
        .eq('user_id', user!.id);
      if (error) throw error;
      return (data ?? [])
        .filter((row: any) => row.organizations && !row.organizations.deleted_at)
        .map((row: any) => ({
          org_id: row.org_id,
          org_name: row.organizations.name,
          org_slug: row.organizations.slug,
          role: row.role as OrgRole,
        }));
    },
  });

  const memberships = query.data ?? [];
  const priority: OrgRole[] = ['owner', 'admin', 'instructor', 'student'];
  const current = [...memberships].sort(
    (a, b) => priority.indexOf(a.role) - priority.indexOf(b.role),
  )[0];

  return {
    memberships,
    currentOrg: current,
    canManage: !!current && ['owner', 'admin', 'instructor'].includes(current.role),
    isOwnerOrAdmin: !!current && ['owner', 'admin'].includes(current.role),
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
